import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Modal, Box, Typography, Button, CircularProgress, Divider,
  IconButton, Chip, Link, Checkbox, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import apiFetch from '../utils/apiFetch';
import WaveSurfer from 'wavesurfer.js';

// --- Silence Detection Helper ---
const detectSilence = (buffer, threshold = 0.15, minDuration = 0.5) => {
  if (!buffer) return [];
  const channelData = buffer.getChannelData(0);
  const sampleRate = buffer.sampleRate;
  const chunkSize = Math.floor(sampleRate * 0.1); 
  let silences = [];
  let isSilent = false;
  let silenceStart = 0;

  for (let i = 0; i < channelData.length; i += chunkSize) {
    let max = 0;
    for (let j = 0; j < chunkSize && i + j < channelData.length; j++) {
      const absVal = Math.abs(channelData[i + j]);
      if (absVal > max) max = absVal;
    }

    if (max < threshold) {
      if (!isSilent) {
        isSilent = true;
        silenceStart = i / sampleRate;
      }
    } else {
      if (isSilent) {
        isSilent = false;
        const silenceEnd = i / sampleRate;
        if (silenceEnd - silenceStart >= minDuration) {
          silences.push({ start: silenceStart, end: silenceEnd });
        }
      }
    }
  }
  
  if (isSilent) {
    const silenceEnd = channelData.length / sampleRate;
    if (silenceEnd - silenceStart >= minDuration) {
      silences.push({ start: silenceStart, end: silenceEnd });
    }
  }
  return silences;
};

// --- Secure Audio Player Component ---
const SecureAudioPlayer = ({ fileId, fallbackUrl, isActive, onPlay, onEnded, skipSilence }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const silentRegionsRef = useRef([]);

  const skipSilenceRef = useRef(skipSilence);
  useEffect(() => {
    skipSilenceRef.current = skipSilence;
  }, [skipSilence]);

  const callbacksRef = useRef({ onPlay, onEnded });
  useEffect(() => {
    callbacksRef.current = { onPlay, onEnded };
  }, [onPlay, onEnded]);

  useEffect(() => {
    if (!fileId) {
      setAudioUrl(fallbackUrl);
      setLoading(false);
      return;
    }

    let objectUrl = null;

    const loadAudioSecurely = async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL}/api/recordings/stream/${fileId}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: { authtoken: localStorage.getItem("loggedintoken") }
        });

        if (!response.ok) throw new Error("Failed to fetch secure stream");

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setAudioUrl(objectUrl);

      } catch (err) {
        console.error("Audio Load Error:", err);
        setAudioUrl(fallbackUrl);
      } finally {
        setLoading(false);
      }
    };

    loadAudioSecurely();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId, fallbackUrl]);

  useEffect(() => {
    if (loading || !audioUrl || !waveformRef.current) return;

    wavesurferRef.current = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#b0bec5',
      progressColor: '#1976d2',
      cursorColor: '#1976d2',
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 40,
      url: audioUrl
    });

    wavesurferRef.current.on('ready', () => {
      setIsReady(true);
      const buffer = wavesurferRef.current.getDecodedData 
        ? wavesurferRef.current.getDecodedData() 
        : (wavesurferRef.current.backend ? wavesurferRef.current.backend.buffer : null);
        
      if (buffer) {
        silentRegionsRef.current = detectSilence(buffer);
      }
    });

    wavesurferRef.current.on('play', () => {
      setIsPlaying(true);
      if (callbacksRef.current.onPlay) callbacksRef.current.onPlay();
    });

    wavesurferRef.current.on('pause', () => setIsPlaying(false));

    wavesurferRef.current.on('finish', () => {
      setIsPlaying(false);
      if (callbacksRef.current.onEnded) callbacksRef.current.onEnded();
    });

    const onProgress = (currentTime) => {
      if (!skipSilenceRef.current || silentRegionsRef.current.length === 0) return;
      
      const regions = silentRegionsRef.current;
      for (let i = 0; i < regions.length; i++) {
        if (currentTime >= regions[i].start && currentTime < regions[i].end - 0.2) {
          const target = regions[i].end;
          if (typeof wavesurferRef.current.setTime === 'function') {
            wavesurferRef.current.setTime(target);
          } else {
            wavesurferRef.current.seekTo(target / wavesurferRef.current.getDuration());
          }
          break;
        }
      }
    };

    wavesurferRef.current.on('audioprocess', onProgress);
    wavesurferRef.current.on('timeupdate', onProgress);

    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }
      setIsReady(false);
      setIsPlaying(false);
    };
  }, [loading, audioUrl]);

  useEffect(() => {
    if (!wavesurferRef.current || !isReady) return;

    if (isActive) {
      wavesurferRef.current.play().catch(e => console.error("Autoplay prevented:", e));
    } else {
      wavesurferRef.current.pause();
    }
  }, [isActive, isReady]);

  const togglePlayPause = () => {
    if (wavesurferRef.current && isReady) {
      wavesurferRef.current.playPause();
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mt: 1, gap: 1.5 }}>
      <IconButton
        onClick={togglePlayPause}
        color="primary"
        disabled={!isReady}
        sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}
      >
        {!isReady ? (
          <CircularProgress size={20} color="inherit" />
        ) : isPlaying ? (
          <PauseIcon />
        ) : (
          <PlayArrowIcon />
        )}
      </IconButton>

      <Box
        ref={waveformRef}
        sx={{
          flexGrow: 1,
          height: '40px',
          cursor: isReady ? 'pointer' : 'default',
          userSelect: 'none',
          opacity: isReady ? 1 : 0.5,
          transition: 'opacity 0.3s'
        }}
      />
    </Box>
  );
};

// --- Modal Style ---
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 650,
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 3,
  p: 0,
  overflow: 'hidden'
};

const InterviewAudioPopup = ({ open, onClose, interviewId, intervieweeName, hrName, showAlert }) => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeChunkUid, setActiveChunkUid] = useState(null);
  const [skipSilence, setSkipSilence] = useState(true);

  const fetchRecordings = useCallback(async () => {
    if (!interviewId) return;
    setLoading(true);
    setRecordings([]);
    setActiveChunkUid(null);

    try {
      const url = `${process.env.REACT_APP_API_URL}/api/recordings/${interviewId}`;
      const res = await apiFetch(url, {
        method: "GET",
        headers: { authtoken: localStorage.getItem("loggedintoken") }
      });

      const data = res.status === 200 ? await res.json() : await res.text();

      if (data?.success && data?.data) {
        
        // --- CHRONOLOGICAL TIME-GAP CLUSTERING ALGORITHM ---
        
        // 1. Flatten every single clip into one array
        let allChunks = [];
        const items = Array.isArray(data.data) ? data.data : [data.data];
        
        items.forEach(item => {
          if (item.chunks && Array.isArray(item.chunks)) {
            item.chunks.forEach(c => allChunks.push(c));
          } else {
            allChunks.push(item);
          }
        });

        // 2. Sort all clips strictly by their START time
        allChunks.sort((a, b) => {
          const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
          const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
          return timeA - timeB;
        });

        // 3. Cluster into Sessions based on idle time
        const GAP_THRESHOLD_MS = 60000; // Define a "major gap" as 60 seconds (adjust if needed)
        const clusteredSessions = [];
        let currentSession = null;
        let globalIndex = 0;

        allChunks.forEach(chunk => {
          // Normalize chunk data
          const currentChunk = { ...chunk, uid: chunk._id || `chunk-${globalIndex++}` };

          if (!currentSession) {
            // Initialize the very first session
            currentSession = {
              recordingStartTime: currentChunk.startTime,
              chunks: [currentChunk]
            };
          } else {
            // Compare the start of THIS chunk to the end of the LAST chunk
            const lastChunk = currentSession.chunks[currentSession.chunks.length - 1];
            
            const lastEndTime = lastChunk.endTime ? new Date(lastChunk.endTime).getTime() : 0;
            const currentStartTime = currentChunk.startTime ? new Date(currentChunk.startTime).getTime() : 0;
            
            // Calculate time gap in milliseconds
            const gap = currentStartTime - lastEndTime;

            // If the gap exceeds our threshold, start a new session bucket
            if (lastEndTime > 0 && currentStartTime > 0 && gap > GAP_THRESHOLD_MS) {
              clusteredSessions.push(currentSession);
              currentSession = {
                recordingStartTime: currentChunk.startTime,
                chunks: [currentChunk]
              };
            } else {
              // Otherwise, it belongs to the continuous active session
              currentSession.chunks.push(currentChunk);
            }
          }
        });

        // Push the final session if it exists
        if (currentSession) {
          clusteredSessions.push(currentSession);
        }

        setRecordings(clusteredSessions);
      } else if (res.status !== 404) {
        if (showAlert) showAlert("Error fetching recordings", "error");
      }
    } catch (error) {
      if (showAlert) showAlert("Network error fetching recordings", "error");
      console.error("Audio Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  }, [interviewId, showAlert]);

  useEffect(() => {
    if (open && interviewId) {
      fetchRecordings();
    } else if (!open) {
      setActiveChunkUid(null);
    }
  }, [open, interviewId, fetchRecordings]);

  const flatChunks = useMemo(() => {
    return recordings.flatMap(session => session.chunks || []);
  }, [recordings]);

  const handleChunkEnded = (currentUid) => {
    const currentIndex = flatChunks.findIndex(c => c.uid === currentUid);

    if (currentIndex !== -1 && currentIndex < flatChunks.length - 1) {
      const nextChunk = flatChunks[currentIndex + 1];
      setActiveChunkUid(nextChunk.uid);
    } else {
      setActiveChunkUid(null);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  
  const formatSessionDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? 'Unknown Date' : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
  };

  const getDuration = (start, end) => {
    if (!start || !end) return "Unknown";
    const diffMs = new Date(end) - new Date(start);
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const extractDriveFileId = (url) => {
    if (!url) return null;
    const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/) || url.match(/id=([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="audio-popup-title">
      <Box sx={modalStyle}>

        <Box sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          fontWeight: '600',
          justifyContent: 'center',
          p: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#ffffff',
          lineHeight: 1.3
        }}>
          
          <Box sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}>
            <FormControlLabel
              control={
                <Checkbox 
                  size="small" 
                  checked={skipSilence} 
                  onChange={(e) => setSkipSilence(e.target.checked)} 
                  color="primary"
                />
              }
              label={<Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>Skip Silence</Typography>}
              sx={{ m: 0 }}
            />
          </Box>

          {intervieweeName ? `${intervieweeName}'s interview by ${hrName}` : 'Interview Audio Recordings'}

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'text.secondary',
              '&:hover': { color: 'error.main', bgcolor: 'rgba(211, 47, 47, 0.04)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : recordings.length === 0 ? (
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 6 }}>
              No audio recordings found for this interview.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {recordings.map((session, index) => (

                <Box key={session.recordingStartTime || index} sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eaeaea' }}>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight="700" color="primary.main">
                      Session {index + 1}
                    </Typography>
                    <Chip
                      label={formatSessionDate(session.recordingStartTime)}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {session.chunks && session.chunks.map((chunk, chunkIdx) => {
                      const fileId = extractDriveFileId(chunk.url);

                      return (
                        <Box key={chunk.uid} sx={{
                          p: 2,
                          bgcolor: '#ffffff',
                          borderRadius: 2,
                          borderLeft: '4px solid #1976d2',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                        }}>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="700" color="text.primary">
                              Clip {chunkIdx + 1}
                            </Typography>

                            {chunk.url && (
                              <Button
                                component={Link}
                                href={chunk.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0 }}
                              >
                                Google Drive
                              </Button>
                            )}
                          </Box>

                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                            <strong>Start:</strong> {formatTime(chunk.startTime)} &nbsp;&bull;&nbsp;
                            <strong>End:</strong> {formatTime(chunk.endTime)} &nbsp;&bull;&nbsp;
                            <strong>Duration:</strong> {getDuration(chunk.startTime, chunk.endTime)}
                          </Typography>

                          <SecureAudioPlayer
                            fileId={fileId}
                            fallbackUrl={chunk.url}
                            isActive={activeChunkUid === chunk.uid}
                            onPlay={() => setActiveChunkUid(chunk.uid)}
                            onEnded={() => handleChunkEnded(chunk.uid)}
                            skipSilence={skipSilence}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(InterviewAudioPopup);